import type { AppUser } from "@/lib/types/next-auth";
import { UserRoles } from "@/lib/types/user-roles";
import { getActiveRole } from "@/lib/utils/roles";
import {
  isNotificationUnread,
  normalizeNotificationId,
} from "./notification-read-state";

type NotificationCopy = {
  type?: string | null;
  title?: string | null;
  message?: string | null;
  id?: unknown;
};

type UnreadListItem = NotificationCopy & {
  id?: unknown;
  isRead?: boolean;
};

function itemId(item: UnreadListItem): string {
  return normalizeNotificationId(item.id);
}

function notificationHaystack(notification: NotificationCopy): string {
  return [notification.type, notification.title, notification.message]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
}

/**
 * Inbox follows the current shell, not every role on the account.
 * `/portal` is always Member, even if the same user is Admin/Teacher in `/admin`.
 */
export function resolveNotificationAudience(
  pathname: string | null | undefined,
  user: AppUser | null | undefined,
): UserRoles | null {
  if (pathname?.startsWith("/portal")) {
    return UserRoles.Member;
  }

  const role = getActiveRole(user);
  if (
    role === UserRoles.Admin ||
    role === UserRoles.Teacher ||
    role === UserRoles.Staff ||
    role === UserRoles.Member
  ) {
    return role;
  }

  return null;
}

export function isPaymentNotification(notification: NotificationCopy): boolean {
  return notificationHaystack(notification).includes("payment");
}

/** Parent/family billing and enrollment — Member portal only. */
export function isFamilyNotification(notification: NotificationCopy): boolean {
  const haystack = notificationHaystack(notification);
  return (
    haystack.includes("payment") ||
    haystack.includes("enroll") ||
    haystack.includes("makeup") ||
    haystack.includes("invoice")
  );
}

/** Class/attendance copy that both parents and studio roles may need. */
export function isScheduleNotification(notification: NotificationCopy): boolean {
  const haystack = notificationHaystack(notification);
  return haystack.includes("class") || haystack.includes("attendance");
}

export function shouldShowNotificationForRole(
  notification: NotificationCopy,
  role: string | null | undefined,
): boolean {
  if (!role) return true;

  if (role === UserRoles.Member) {
    return (
      isFamilyNotification(notification) || isScheduleNotification(notification)
    );
  }

  // Teacher and Staff are studio-facing, not family billing.
  if (role === UserRoles.Teacher || role === UserRoles.Staff) {
    return !isFamilyNotification(notification);
  }

  return true;
}

export function filterNotificationsVisibleToRole<T extends NotificationCopy>(
  notifications: T[],
  role: string | null | undefined,
): T[] {
  if (!role) return notifications;
  return notifications.filter((notification) =>
    shouldShowNotificationForRole(notification, role),
  );
}

export function applyNotificationAudienceToUnreadSummary<
  T extends {
    items?: Array<NotificationCopy> | null;
    totalCount?: unknown;
  },
>(data: T | undefined, role: string | null | undefined): T | undefined {
  if (!data || !role) return data;

  const items = data.items ?? [];
  const visibleItems = filterNotificationsVisibleToRole(items, role);
  if (visibleItems.length === items.length) return data;

  // Don't reuse the API total: remaining pages may belong to another role.
  return {
    ...data,
    items: visibleItems as T["items"],
    totalCount: visibleItems.length,
  } as T;
}

export function hidesFamilyNotificationTypes(
  role: string | null | undefined,
): boolean {
  return role === UserRoles.Teacher || role === UserRoles.Staff;
}

/** Inboxes that hide some of the shared user feed. Admin does not. */
export function filtersNotificationsByAudience(
  role: string | null | undefined,
): boolean {
  return role === UserRoles.Member || hidesFamilyNotificationTypes(role);
}

function visibleUnreadCount<T extends UnreadListItem>(
  items: T[],
  role: string | null | undefined,
  markedReadIdSet: Set<string>,
  options?: { trustIsRead?: boolean; trustedReadIds?: Set<string> },
): { visible: T[]; hidden: number; visibleNotDismissed: number; unread: T[] } {
  const trustIsRead = options?.trustIsRead ?? false;
  const trustedReadIds = options?.trustedReadIds;
  const visible = filterNotificationsVisibleToRole(items, role);
  const unread = visible.filter((notification) => {
    const id = itemId(notification);
    if (markedReadIdSet.has(id)) return false;
    if (trustedReadIds?.has(id)) return false;
    if (trustIsRead) {
      return isNotificationUnread(
        { id: itemId(notification), isRead: notification.isRead === true },
        markedReadIdSet,
      );
    }
    return true;
  });
  return {
    visible,
    hidden: items.length - visible.length,
    visibleNotDismissed: unread.length,
    unread,
  };
}

/**
 * Bell badge: GET `/notifications` `isRead` is source of truth when cached.
 * `/unread` still returns already-read rows and often marks real unread as
 * `isRead: true`, so that flag is ignored unless the inbox list contradicts it.
 */
export function getBellUnreadCount(params: {
  role: string | null | undefined;
  apiUnreadCount: number;
  unreadItems: UnreadListItem[];
  unreadTotalCount?: number | null;
  hasUnreadListData: boolean;
  markedReadIdSet: Set<string>;
  trustedItems?: UnreadListItem[];
}): number {
  const {
    role,
    apiUnreadCount,
    unreadItems,
    unreadTotalCount,
    hasUnreadListData,
    markedReadIdSet,
    trustedItems = [],
  } = params;

  const trustedUnread = visibleUnreadCount(
    trustedItems,
    role,
    markedReadIdSet,
    { trustIsRead: true },
  ).unread;

  if (trustedItems.length > 0) {
    return trustedUnread.length;
  }

  if (!hasUnreadListData) {
    return filtersNotificationsByAudience(role) ? 0 : apiUnreadCount;
  }

  const { hidden, visibleNotDismissed } = visibleUnreadCount(
    unreadItems,
    role,
    markedReadIdSet,
  );
  const fetchedTotal = Number(unreadTotalCount ?? unreadItems.length);

  if (unreadItems.length === 0 || unreadItems.length >= fetchedTotal) {
    return visibleNotDismissed;
  }

  if (!filtersNotificationsByAudience(role) || hidden === 0) {
    return apiUnreadCount;
  }

  return Math.max(0, apiUnreadCount - hidden);
}

export function mergeBellUnreadNotifications<T extends UnreadListItem>(
  unreadItems: T[],
  trustedItems: T[],
  role: string | null | undefined,
  markedReadIdSet: Set<string>,
): T[] {
  if (trustedItems.length > 0) {
    return visibleUnreadCount(trustedItems, role, markedReadIdSet, {
      trustIsRead: true,
    }).unread;
  }

  return visibleUnreadCount(unreadItems, role, markedReadIdSet).unread;
}
